import {
  SKILL_EDITED_FIELD_LABEL_KEYS,
  SKILL_PERMISSION_CHANGED_BODY_KEYS,
  SKILL_SHARED_BODY_KEYS,
} from "../constants/notifications";
import type {
  IFormatSkillEditedFieldsProps,
  ISkillNotificationView,
  TSkillEditedFieldLabelKey,
  TSkillNotification,
  TSkillPermissionChangedBodyKey,
  TSkillSharedBodyKey,
} from "../types/notifications";
import {
  ENotificationType,
  ESkillEditedField,
  ESkillNotificationTone,
} from "../types/notifications";
import { ESkillShareRole } from "../types/skills";

const SKILL_SHARE_ROLES: readonly string[] = Object.values(ESkillShareRole);

const SKILL_EDITED_FIELDS: readonly string[] = Object.values(ESkillEditedField);

export const isSkillShareRole = (value: string): value is ESkillShareRole =>
  SKILL_SHARE_ROLES.includes(value);

export const isSkillEditedField = (value: string): value is ESkillEditedField =>
  SKILL_EDITED_FIELDS.includes(value);

// NOTE: NSAI-6952 — `permissionLevel` travels as a free string so an older
// client survives a role the server adds later. Such a role reads as the
// viewer sentence: the card must never claim more access than was granted.
const toKnownShareRole = (permissionLevel: string): ESkillShareRole =>
  isSkillShareRole(permissionLevel) ? permissionLevel : ESkillShareRole.VIEWER;

export const resolveSkillSharedBodyKey = (
  permissionLevel: string,
): TSkillSharedBodyKey =>
  SKILL_SHARED_BODY_KEYS[toKnownShareRole(permissionLevel)];

export const resolveSkillPermissionChangedBodyKey = (
  permissionLevel: string,
): TSkillPermissionChangedBodyKey =>
  SKILL_PERMISSION_CHANGED_BODY_KEYS[toKnownShareRole(permissionLevel)];

// NOTE: NSAI-6952 — null for a field this client does not know, so the card
// can show the raw field name instead of a missing-key placeholder.
export const resolveSkillEditedFieldLabelKey = (
  field: string,
): TSkillEditedFieldLabelKey | null =>
  isSkillEditedField(field) ? SKILL_EDITED_FIELD_LABEL_KEYS[field] : null;

// NOTE: NSAI-6952 — the changed fields as one readable list: a known field
// through its i18n label, a field this client does not know by its raw name.
export const formatSkillEditedFields = ({
  changedFields,
  translateLabel,
}: IFormatSkillEditedFieldsProps): string =>
  changedFields
    .map((field) => {
      const labelKey = resolveSkillEditedFieldLabelKey(field);

      return labelKey === null ? field : translateLabel(labelKey);
    })
    .join(", ");

// NOTE: NSAI-6952 — what a skill notification card says and whether it offers
// "View skill". Every card but the revoked one opens the skill: after a
// revocation the skill is exactly what the reader can no longer open.
export const resolveSkillNotificationView = (
  notification: TSkillNotification,
): ISkillNotificationView => {
  const { skillUuid, skillName, projectUuid } = notification.data;
  const base = {
    skillUuid,
    projectUuid,
    changedFields: [],
    tone: ESkillNotificationTone.ACCENT,
    hasViewSkillCta: true,
  };

  switch (notification.type) {
    case ENotificationType.SKILL_CREATED:
      return {
        ...base,
        bodyKey: "type.skill_created.body",
        bodyParams: {
          creatorName: notification.data.creatorName,
          skillName,
          projectTitle: notification.data.projectTitle,
        },
      };
    case ENotificationType.SKILL_SHARED:
      return {
        ...base,
        bodyKey: resolveSkillSharedBodyKey(notification.data.permissionLevel),
        bodyParams: { ownerName: notification.data.ownerName, skillName },
      };
    case ENotificationType.SKILL_PERMISSION_CHANGED:
      return {
        ...base,
        bodyKey: resolveSkillPermissionChangedBodyKey(
          notification.data.permissionLevel,
        ),
        bodyParams: { ownerName: notification.data.ownerName, skillName },
      };
    case ENotificationType.SKILL_ACCESS_REVOKED:
      return {
        ...base,
        tone: ESkillNotificationTone.DESTRUCTIVE,
        hasViewSkillCta: false,
        bodyKey: "type.skill_access_revoked.body",
        bodyParams: { ownerName: notification.data.ownerName, skillName },
      };
    // NOTE: Only SKILL_EDITED is left here — TypeScript narrows the union in
    // `default`, so `data` is typed as the edited payload.
    default:
      return {
        ...base,
        changedFields: notification.data.changedFields,
        bodyKey:
          notification.data.changedFields.length > 0
            ? "type.skill_edited.body"
            : "type.skill_edited.body_no_fields",
        bodyParams: { editorName: notification.data.editorName, skillName },
      };
  }
};
